// ui/useTrickSequence.js: #bug4 rAF driver for the trick sequencer.
//
// Wraps createTrickSequencer (pure state machine) in a Preact hook that:
//   1. feeds each new snapshot to the sequencer (observe), and
//   2. runs a rAF clock while a sequence is in flight, re-rendering the view
//      with the current frame so the trick area can draw the 4th-card slide
//      and the sweep-to-winner.
//
// The returned `frame` is the same shape createTrickSequencer.frameAt emits
// (trick_plays + slide + trick_resolution), or null when idle. The view
// overrides the (already-empty) snapshot trick_plays with frame.trick_plays
// during the window so the four cards stay on screen through the sweep.
//
// Mirrors useCardPlayAnimation's lifecycle: one sequencer instance per mount,
// rAF cancelled on unmount.

import { useEffect, useRef, useState } from 'preact/hooks';
import { createTrickSequencer } from './trick-sequencer.js';

function nowMs() {
  if (typeof performance !== 'undefined' && performance.now) return performance.now();
  return Date.now();
}

/**
 * @typedef {{
 *   trick_plays: Array<{seat:number, card_id:string}>,
 *   slide: {seat:number, card_id:string, t:number} | null,
 *   trick_resolution: {winner_seat:number, elapsed_ms:number, reduced_motion:boolean} | null,
 *   done: boolean,
 * }} TrickSequenceFrame
 */

/**
 * @param {{ snapshot: any, reducedMotion?: boolean }} opts
 * @returns {TrickSequenceFrame | null}
 */
export function useTrickSequence(opts) {
  const { snapshot, reducedMotion = false } = opts;

  const seqRef = useRef(/** @type {ReturnType<typeof createTrickSequencer>|null} */ (null));
  // Rebuild the sequencer if the reduced-motion preference flips so the
  // machine's branch matches the current OS setting.
  const rmRef = useRef(reducedMotion);
  if (seqRef.current === null || rmRef.current !== reducedMotion) {
    seqRef.current = createTrickSequencer({ reducedMotion });
    rmRef.current = reducedMotion;
  }

  // The current frame. A new snapshot is fed to the sequencer during
  // render, so the frame that first draws the 4th card's snapshot (whose
  // trick_plays is already empty) already draws the reconstructed trick.
  // (Feeding it in an effect drew an empty trick area for one frame.)
  const frameRef = useRef(/** @type {TrickSequenceFrame | null} */ (null));
  const seenSnapshotRef = useRef(/** @type {any} */ (undefined));
  const [, setTick] = useState(0);
  const rafRef = useRef(/** @type {number|null} */ (null));
  const runningRef = useRef(false);

  if (snapshot !== seenSnapshotRef.current) {
    seenSnapshotRef.current = snapshot;
    const seq = seqRef.current;
    seq.observe(snapshot, nowMs());
    // Always publish the current frame for this snapshot (covers the
    // reduced-motion hold + the first frame of a new sequence).
    frameRef.current = seq.frameAt(nowMs());
  }

  // Snapshot effect: if the sequencer started (or is mid-) a sequence,
  // run the rAF loop until the sequence elapses or is preempted.
  useEffect(() => {
    const seq = seqRef.current;
    if (!seq) return;
    if (seq.isActive() && !runningRef.current) {
      runningRef.current = true;
      const loop = () => {
        const s = seqRef.current;
        if (!s) { runningRef.current = false; rafRef.current = null; return; }
        const f = s.frameAt(nowMs());
        frameRef.current = f;
        setTick((n) => n + 1);
        if (f === null || !s.isActive()) {
          runningRef.current = false;
          rafRef.current = null;
          return;
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    }
  }, [snapshot]);

  // Cleanup on unmount.
  useEffect(() => () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    runningRef.current = false;
    seqRef.current?.reset();
  }, []);

  return frameRef.current;
}
